import { useEffect, useState } from "react";
import { getTenants, type Tenant } from "@/api";
import { useAuth } from "@/auth";

export interface TenantsState {
  tenants: Tenant[];
  tenant: string;
  setTenant: (key: string) => void;
  error: string | null;
}

/**
 * Loads the admin's visible tenants and tracks the currently-selected one,
 * defaulting to the first tenant returned.
 *
 * Carries the same StrictMode/unmount `active` guard as {@link useResource}:
 * a stale (doubled) run must not overwrite the selection. The api client reads
 * its own creds, so this hook never threads them. It re-runs when the auth
 * `status` changes (login/logout) — keyed on the auth *intent*, never on
 * `creds` truthiness, so the Google cookie lane (`status === "cookie"`,
 * `creds === null`) loads tenants just like the Basic lane (ADR-0001).
 */
export function useTenants(): TenantsState {
  const { status } = useAuth();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenant, setTenant] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setError(null);
    getTenants()
      .then((r) => {
        if (!active) return; // a stale (StrictMode-doubled) run must not select
        setTenants(r.tenants);
        if (r.tenants[0]) setTenant(r.tenants[0].key);
      })
      .catch(() => active && setError("Could not load tenants."));
    return () => {
      active = false;
    };
  }, [status]);

  return { tenants, tenant, setTenant, error };
}
